// src/services/attachmentsService.js
// *******************************************************************************
// ATTACHMENTS SERVICE - Frontend client for attachments API
// *******************************************************************************
// Handles file uploads to Firebase Storage via backend
// Domain data (category, label, tags) lives in parent's attachments array
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import axiosService from './axiosService';
import { API, apiUrl } from 'constants/Api';

// *******************************************************************************
// HELPER FUNCTIONS
// *******************************************************************************

/**
 * Calculate SHA-256 hash of a file
 * @param {File|Blob} file - File to hash
 * @returns {Promise<string>} - Hash string prefixed with 'sha256:'
 */
async function calculateFileHash(file) {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return `sha256:${hashHex}`;
}

/**
 * Convert SVG blob to base64 PNG thumbnail
 * @param {Blob} svgBlob - SVG blob
 * @param {number} size - Thumbnail size
 * @returns {Promise<string>} - Base64 data URL
 */
async function svgToBase64Thumb(svgBlob, size = 128) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const svgDataUrl = reader.result;

      const img = new Image();
      img.crossOrigin = 'anonymous'; // Enable CORS
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, size, size);

          const base64 = canvas.toDataURL('image/png', 0.8);
          resolve(base64);
        } catch (err) {
          console.warn('Canvas drawImage failed:', err);
          resolve(null); // Return null instead of rejecting
        }
      };
      img.onerror = err => {
        console.warn('Image load failed for SVG thumbnail:', err);
        resolve(null); // Return null instead of rejecting
      };
      img.src = svgDataUrl;
    };
    reader.onerror = err => {
      console.warn('FileReader failed:', err);
      resolve(null); // Return null instead of rejecting
    };
    reader.readAsDataURL(svgBlob);
  });
}

/**
 * Convert image blob to base64 thumbnail
 * @param {Blob} imageBlob - Image blob
 * @param {number} size - Thumbnail size
 * @returns {Promise<string>} - Base64 data URL
 */
async function imageToBase64Thumb(imageBlob, size = 128) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');

        // Preserve PNG transparency, use white background for JPEG
        const isPng = imageBlob.type === 'image/png';
        const isWebp = imageBlob.type === 'image/webp';
        const preserveTransparency = isPng || isWebp;

        if (!preserveTransparency) {
          // Fill with white background for non-transparent formats
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, size, size);
        }

        ctx.drawImage(img, 0, 0, size, size);

        // Use PNG for transparent images, JPEG for others
        const mimeType = preserveTransparency ? 'image/png' : 'image/jpeg';
        const quality = preserveTransparency ? undefined : 0.7;
        const base64 = canvas.toDataURL(mimeType, quality);
        resolve(base64);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(imageBlob);
  });
}

// *******************************************************************************
// SERVICE FUNCTIONS
// *******************************************************************************

/**
 * Check if file already exists by hash (deduplication)
 * @param {string} contentHash - SHA-256 hash of file
 * @returns {Promise<Object>} - { duplicate: boolean, attachment?: Object }
 */
async function checkDuplicate(contentHash) {
  const [data] = await axiosService.get({
    url: apiUrl.build({
      url: API.ATTACHMENTS.CHECK_DUPLICATE,
      query: { contentHash },
    }),
    returnAsObject: true,
  });

  return data;
}

/**
 * Upload file to backend (which uploads to Firebase Storage)
 * @param {Object} params - Upload parameters
 * @param {File|Blob} params.file - File to upload
 * @param {boolean} [params.checkDuplicate=true] - Check for duplicate before upload
 * @param {boolean} [params.generateThumb=true] - Generate base64 thumbnail
 * @returns {Promise<Object>} - { duplicate: boolean, attachment: Object }
 */
async function upload({ file, checkDuplicate: shouldCheckDuplicate = true, generateThumb = true }) {
  // Calculate hash for deduplication
  let contentHash = null;
  if (shouldCheckDuplicate) {
    contentHash = await calculateFileHash(file);

    // Check for existing file
    const duplicateCheck = await checkDuplicate(contentHash);
    if (duplicateCheck?.duplicate) {
      return duplicateCheck;
    }
  }

  // Generate thumbnail for images
  let base64Thumb = null;
  if (generateThumb && file.type?.startsWith('image/')) {
    try {
      if (file.type === 'image/svg+xml') {
        base64Thumb = await svgToBase64Thumb(file);
      } else {
        base64Thumb = await imageToBase64Thumb(file);
      }
    } catch (err) {
      console.warn('Failed to generate thumbnail:', err);
    }
  }

  // Prepare form data
  const formData = new FormData();
  formData.append('file', file, file.name || 'file');
  if (contentHash) formData.append('contentHash', contentHash);
  if (base64Thumb) formData.append('base64Thumb', base64Thumb);

  // Note: Don't set Content-Type header - browser will set it automatically with boundary
  const [data] = await axiosService.post({
    url: API.ATTACHMENTS.UPLOAD,
    data: formData,
    returnAsObject: true,
  });

  return data;
}

/**
 * Upload avatar from DiceBear URL
 * @param {Object} params - Upload parameters
 * @param {string} params.dicebearUrl - DiceBear avatar URL
 * @returns {Promise<Object>} - { duplicate: boolean, attachment: Object }
 */
async function uploadDicebearAvatar({ dicebearUrl }) {
  // Fetch SVG from DiceBear
  const response = await fetch(dicebearUrl);
  if (!response.ok) throw new Error('Failed to fetch avatar from DiceBear');

  const svgBlob = await response.blob();

  // Create file from blob
  const filename = `avatar-${Date.now()}.svg`;
  const file = new File([svgBlob], filename, { type: 'image/svg+xml' });

  // Upload using standard upload function
  return upload({
    file,
    checkDuplicate: false, // DiceBear avatars are unique per seed/style
    generateThumb: true,
  });
}

/**
 * Build attachmentInfo object to embed in parent document
 * @param {Object} attachment - Attachment document from API
 * @param {string} category - Category for this attachment (avatar, gallery, document, etc.)
 * @param {Object} options - Additional options (label, sortOrder, isPrimary, description, tags)
 * @returns {Object} - attachmentInfo object ready to embed in parent
 */
function buildAttachmentInfo(attachment, category = 'avatar', options = {}) {
  return {
    attachmentId: attachment._id,
    category: category || 'other',
    storageUrl: attachment.storageUrl,
    thumbUrl: attachment.thumbUrl || attachment.storageUrl,
    base64Thumb: attachment.base64Thumb || null,
    mimeType: attachment.mimeType,
    originalName: attachment.originalName,
    label: options.label || null,
    sortOrder: options.sortOrder || 0,
    isPrimary: options.isPrimary !== undefined ? options.isPrimary : true,
    description: options.description || null,
    tags: options.tags || [],
  };
}

/**
 * Mark attachment(s) as persisted
 * @param {string|string[]} attachmentIds - Attachment ID(s) to persist
 * @returns {Promise<Object>} - { success: boolean, modifiedCount: number }
 */
async function persist(attachmentIds) {
  const ids = Array.isArray(attachmentIds) ? attachmentIds : [attachmentIds];

  const [data] = await axiosService.put({
    url: API.ATTACHMENTS.PERSIST,
    data: { attachmentIds: ids },
    returnAsObject: true,
  });

  return data;
}

/**
 * Get attachment by ID
 * @param {string} attachmentId - Attachment ID
 * @returns {Promise<Object>} - Attachment document
 */
async function getById(attachmentId) {
  const [data] = await axiosService.get({
    url: apiUrl.build({
      url: API.ATTACHMENTS.DETAIL,
      params: { _id: attachmentId },
    }),
    returnAsObject: true,
  });

  return data;
}

/**
 * Delete attachment
 * @param {string} attachmentId - Attachment ID
 * @param {boolean} [removeFromStorage=true] - Also remove from Firebase Storage
 * @returns {Promise<Object>} - Deleted attachment
 */
async function remove(attachmentId, removeFromStorage = true) {
  const [data] = await axiosService.delete({
    url: apiUrl.build({
      url: API.ATTACHMENTS.DELETE,
      params: { _id: attachmentId },
      query: { removeFromStorage },
    }),
    returnAsObject: true,
  });

  return data;
}

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default {
  // Helpers
  calculateFileHash,
  svgToBase64Thumb,
  imageToBase64Thumb,
  buildAttachmentInfo,

  // API functions
  checkDuplicate,
  upload,
  uploadDicebearAvatar,
  persist,
  getById,
  remove,
};
